import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import config from '../../config'
import JoinStartPage from './JoinStartPage'
import JoinStudioPage from './JoinStudioPage'
import JoinPlanPage from './JoinPlanPage'
import { PlanType } from './shared'
import { useBilling } from './hooks'
import PaymentPage from 'components/PaymentPage'
import heapService from 'services/heapService'

const StepId = {
  START: 1,
  STUDIO: 2,
  PLAN: 3,
  PAYMENT: 4
}

const COPY_CONFIG = {
  regular: {
    banner: null,
    cta: 'Get Started'
  },
  disc50: {
    banner: 'Limited-time sale! Get Wally for $199 (usually $249).',
    cta: 'Get Offer'
  },
  disc75: {
    banner: 'Limited-time sale! Get Wally for $174 (usually $249).',
    cta: 'Get Offer'
  },
  disc100: {
    banner: 'Our biggest gift ever: Get Wally for $149 (normally $249). Plaque Friday ONLY!',
    cta: 'Get Offer'
  }
}

const resolveCopy = (selector) => {
  return COPY_CONFIG[selector] || COPY_CONFIG.regular
}

const JoinFlow = () => {
  const locations = config.LOCATIONS

  const [search] = useSearchParams()

  const [stepId, setStepId] = useState(StepId.START)
  const [customer, setCustomer] = useState(null)
  const [planType, setPlanType] = useState(null)
  const [totalCostInCents, setTotalCostInCents] = useState(0)
  const [locationId, setLocationId] = useState(null)
  const [couponCode, setCouponCode] = useState(search.get('code'))

  const copySelector = search.get('cpy')

  const { pricing, reloadPricing, isPricingLoading, prepSubscription } = useBilling({ couponCode })

  const scrollToTop = () => (
    window.scrollTo({ top: 0, behavior: 'smooth' })
  )
  useEffect(() => {
    scrollToTop()
  }, [stepId])

  const applyCouponCode = ({ code }) => {
    setCouponCode(code)
    if (code === couponCode) {
      reloadPricing()
    }
  }

  const handleCustomerInfo = (info) => {
    setCustomer(info)
    setStepId(StepId.STUDIO)
  }

  const onLocationPicked = (locationId) => {
    setLocationId(locationId)
    setStepId(StepId.PLAN)
  }

  const onPlanSelected = async ({ planType, totalCostInCents }) => {
    setPlanType(planType)
    setTotalCostInCents(totalCostInCents)
    setStepId(StepId.PAYMENT)
  }

  const storeHeapUserProperties = () => {
    heapService.storeUserProperties({
      phone: customer.phone,
      email: customer.email,
      costInCents: totalCostInCents,
      location: locations.find(l => l.id === locationId)?.name,
      planType,
      couponCode
    })
  }

  const resolveCouponCode = () => {
    return (planType === PlanType.ANNUAL)
      ? couponCode
      : null
  }

  const handleSubscriptionPrep = () => {
    storeHeapUserProperties()

    return prepSubscription({
      billing: {
        planType,
        couponCode: resolveCouponCode()
      },
      patient: {
        email: customer.email,
        firstName: customer.firstName,
        lastName: customer.lastName,
        phone: customer.phone
      },
      location: locations.find(l => l.id === locationId)
    })
  }

  return (
    <div>

      {stepId === StepId.START &&
        <JoinStartPage
          onInfoCaptured={handleCustomerInfo}
          couponCode={couponCode}
          copy={resolveCopy(copySelector)}
        />
      }

      {stepId === StepId.STUDIO &&
        <JoinStudioPage
          onLocationPicked={onLocationPicked}
          locations={locations}
        />
      }

      {stepId === StepId.PLAN &&
        <JoinPlanPage
          onPlanSelected={onPlanSelected}
          pricing={pricing}
          isPricingLoading={isPricingLoading}
          reloadPricing={reloadPricing}
          applyCouponCode={applyCouponCode}
        />
      }

      {stepId === StepId.PAYMENT &&
        <PaymentPage
          customer={customer}
          totalCostInCents={totalCostInCents}
          planType={planType}
          prepSubscription={handleSubscriptionPrep}
          viewName="JoinPaymentPage"
          description="Once you join your care team will text you how to book your first appointment. And if you change your mind before your first visit, we’ll refund you 100%."
        />
      }
    </div>
  )
}

export default JoinFlow
